"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SessionProvider } from "next-auth/react";
import { useEffect, useState } from "react";
import { AuthProvider } from "@/contexts/auth-context";
import { useTenantStore } from "@/store/tenant-store";
import { HydrationDebugger } from "@/components/HydrationDebugger";
import { ConfirmDialogProvider } from "@/components/confirm-dialog";
// NOTE: Install sonner first: pnpm add sonner
import { Toaster } from "sonner";

/**
 * Manual hydration trigger for zustand stores using `skipHydration: true`.
 *
 * The tenant store can't auto-hydrate from localStorage during initial
 * render — that would mismatch SSR (which has no localStorage) and cause
 * React #418 cascade across every component reading currentTenantId.
 *
 * This component runs after the initial render's commit phase via
 * useEffect, then triggers persist.rehydrate() which populates the
 * store from localStorage and re-renders downstream consumers.
 *
 * Net effect: components see currentTenantId = null on first paint
 * (matching SSR), then get the real value one frame later. No
 * hydration mismatch, ~1 frame of skeleton state.
 */
function TenantStoreHydrator() {
  useEffect(() => {
    useTenantStore.persist.rehydrate();
  }, []);
  return null;
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            retry: 1,
          },
        },
      })
  );

  return (
    <SessionProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TenantStoreHydrator />
          <HydrationDebugger />
          <ConfirmDialogProvider>
            {children}
          </ConfirmDialogProvider>
          {/* Toast notifications for user feedback */}
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </QueryClientProvider>
    </SessionProvider>
  );
}
